"use client";

import React, {
	useReducer,
	useEffect,
	useMemo,
	useCallback,
	useState,
	useRef,
} from "react";
import { useFundWallet } from "@privy-io/react-auth/solana";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import { useDeviceAndWalletDetection } from "@/hooks/useDeviceAndWalletDetection";
import { Connection, LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import Image from "next/image";
import { createUmiInstance } from "@/lib/umi";
import { getRpcProxyUrl, getBaseUrl, getRpcWebSocketUrl } from "@/lib/rpc-url";
import { fetchAllDigitalAssetWithTokenByOwner } from "@metaplex-foundation/mpl-token-metadata";
import { fromWeb3JsPublicKey } from "@metaplex-foundation/umi-web3js-adapters";
import { useRouter } from "next/navigation";
import { LogOut } from "lucide-react";
import { DisconnectConfirmDialog } from "@/components/UI/disconnect-modal";
import { WalletHeader } from "@/components/UI/wallet-ui/wallet-header";
import { WalletBalance } from "@/components/UI/wallet-ui/wallet-balance";
import { WalletTabs } from "@/components/UI/wallet-ui/wallet-tabs";
import { TokensTab } from "@/components/UI/wallet-ui/wallet-token-tabs";
import { NftsTab } from "@/components/UI/wallet-ui/wallet-nfts-tab";
import { CnftsTab } from "@/components/UI/wallet-ui/wallet-cnfts";
import { PhantomWalletLinker } from "@/components/UI/phantom-wallet-linker";
import { WalletSwitcher } from "@/components/UI/wallet-switcher";
import { MYGEOTOKENS_WALLET_DISPLAY_NAME } from "@/constants/wallet-branding";
import { NFT, WalletAction } from "@/app/types";
import {
	ACTION_TYPES,
	CONFIG,
	initialState,
	walletReducer,
	WalletState,
} from "@/constants/misc.constants";
import { useOptimizedCNFTFetch } from "@/hooks/useOptimizedCNFTFetch";
import { message } from "antd";

const WalletTabsPanel = React.memo(function WalletTabsPanel({
	activeTab,
	compressedNfts,
	handleTabChange,
	balance,
	isNFTLoading,
	nftLoadingProgressText,
	paginatedNFTs,
	handleNFTClick,
	totalPages,
	currentPage,
	handlePrevPage,
	handleNextPage,
	handleRefresh,
	isRefreshing,
	isCNFTOptimizedLoading,
	paginatedCompressedNFTs,
	totalCompressedPages,
	compressedCurrentPage,
	handleCompressedPrevPage,
	handleCompressedNextPage,
	progress,
}: any) {
	return (
		<div className="border-t border-[#231F20] pt-4">
			<WalletTabs
				activeTab={activeTab}
				handleTabChange={handleTabChange}
				compressedNfts={compressedNfts}
			/>

			{activeTab === "tokens" && (
				<div id="wallet-panel-tokens" role="tabpanel" aria-labelledby="wallet-tab-tokens">
					<TokensTab balance={balance} />
				</div>
			)}

			{activeTab === "nfts" && (
				<div id="wallet-panel-nfts" role="tabpanel" aria-labelledby="wallet-tab-nfts">
					<NftsTab
						isNFTLoading={isNFTLoading}
						loadingProgressText={nftLoadingProgressText}
						paginatedNFTs={paginatedNFTs}
						handleNFTClick={handleNFTClick}
						totalPages={totalPages}
						currentPage={currentPage}
						handlePrevPage={handlePrevPage}
						handleNextPage={handleNextPage}
						handleRefresh={handleRefresh}
						isRefreshing={isRefreshing}
					/>
				</div>
			)}

			{activeTab === "cnfts" && (
				<div id="wallet-panel-cnfts" role="tabpanel" aria-labelledby="wallet-tab-cnfts">
					<CnftsTab
						isNFTLoading={isNFTLoading || isCNFTOptimizedLoading}
						paginatedCompressedNFTs={paginatedCompressedNFTs}
						totalCompressedPages={totalCompressedPages}
						compressedCurrentPage={compressedCurrentPage}
						handleCompressedPrevPage={handleCompressedPrevPage}
						handleCompressedNextPage={handleCompressedNextPage}
						handleRefresh={handleRefresh}
						isRefreshing={isRefreshing}
						progress={progress}
						totalCount={compressedNfts.length}
					/>
				</div>
			)}
		</div>
	);
});

const DisconnectedWalletPanel = React.memo(function DisconnectedWalletPanel({
	showPhantomLinker,
	setShowPhantomLinker,
	isMobile,
	hasPhantomExtension,
	handleDirectPhantomConnection,
	isConnectingPhantom,
}: any) {
	return (
		<div className="w-full space-y-3">
			{showPhantomLinker ? (
				<PhantomWalletLinker
					onSuccess={() => {
						setShowPhantomLinker(false);
						window.location.reload();
					}}
					onCancel={() => setShowPhantomLinker(false)}
				/>
			) : !isMobile && hasPhantomExtension ? (
				<div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
					<h3 className="text-xl font-semibold text-white mb-4">
						Connect {MYGEOTOKENS_WALLET_DISPLAY_NAME}
					</h3>
					<button
						onClick={handleDirectPhantomConnection}
						disabled={isConnectingPhantom}
						className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 py-3 px-6 rounded-xl text-white font-medium transition-colors mb-4 flex items-center justify-center gap-3"
					>
						<div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
							<Image
								src="/phantom-icon.svg"
								alt={MYGEOTOKENS_WALLET_DISPLAY_NAME}
								width={16}
								height={16}
								className="phantom-icon"
							/>
						</div>
						{isConnectingPhantom
							? "Connecting..."
							: `Connect ${MYGEOTOKENS_WALLET_DISPLAY_NAME}`}
					</button>
					<p className="text-gray-400 text-sm mb-4">
						Access your existing NFT collection with {MYGEOTOKENS_WALLET_DISPLAY_NAME}
					</p>

					<div className="border-t border-gray-700 pt-4">
						<p className="text-gray-400 text-sm mb-3">Already have an account?</p>
						<button
							onClick={() => setShowPhantomLinker(true)}
							className="w-full bg-gray-600 hover:bg-gray-700 py-2 px-4 rounded-lg text-white text-sm transition-colors"
						>
							Link wallet to existing account
						</button>
					</div>
				</div>
			) : (
				<>
					<div className="bg-mgt-black border border-mgt-green/30 rounded-lg p-4">
						<div className="text-center text-white">
							{isMobile
								? "Please connect your wallet from the header to view your assets."
								: `${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not detected. Please install it or connect from the header.`}
						</div>
					</div>
					<div className="bg-[#231F20]/50 border border-[#918884]/30 rounded-lg p-3">
						<p className="text-[#918884] text-xs text-center">
							<strong>Connection taking too long?</strong> Try refreshing the page
							or disconnecting and reconnecting your wallet.
						</p>
					</div>
				</>
			)}
		</div>
	);
});

const WalletUI: React.FC = () => {
	const {
		publicKey,
		signer,
		isConnected,
		logout,
		authenticated,
		ready,
		user,
		privyUser,
		connect,
	} = usePrivyWallet();
	const { fundWallet } = useFundWallet();
	const { isMobile, hasPhantomExtension } = useDeviceAndWalletDetection();

	const router = useRouter();
	const [rpc, setRPC] = useState<string | undefined>(undefined);
	const [state, dispatch] = useReducer<
		React.Reducer<WalletState, WalletAction>
	>(walletReducer, initialState);
	const [loadedTabs, setLoadedTabs] = useState<{ [key: string]: boolean }>({});
	const [umi, setUmi] = useState<any>(null);
	const [showPhantomLinker, setShowPhantomLinker] = useState(false);
	const [isConnectingPhantom, setIsConnectingPhantom] = useState(false);
	const [nftLoadingProgressText, setNftLoadingProgressText] = useState<string | null>(
		null
	);

	// Track initialization to prevent multiple UMI creations
	const umiInitializedRef = useRef<string | null>(null);
	// Track previous publicKey to detect wallet switches
	const prevPublicKeyRef = useRef<string | null>(null);
	const standardNftFetchAbortRef = useRef<AbortController | null>(null);
	const standardNftFetchRequestIdRef = useRef(0);

	const {
		isLoading,
		isNFTLoading,
		isRefreshing,
		error,
		balance,
		nfts,
		compressedNfts,
		activeTab,
		currentPage,
		compressedCurrentPage,
		copied,
		showDisconnectConfirm,
	} = state;

	const connection = useMemo(() => {
		// Use centralized RPC URL utility to ensure consistent URL construction
		const rpcURL = getRpcProxyUrl();
		setRPC(rpcURL);
		// Disable WebSocket since proxy only supports HTTP
		return new Connection(rpcURL, {
			commitment: "confirmed",
			wsEndpoint: getRpcWebSocketUrl(),
		});
	}, []);

	// Initialize UMI once and only when wallet changes
	useEffect(() => {
		const currentWalletKey = publicKey?.toString();

		if (signer && publicKey && currentWalletKey !== umiInitializedRef.current) {
			try {
				const umiSigner = {
					...signer,
					publicKey: publicKey as any,
					signAllTransactions: async (transactions: any[]) => {
						const signedTransactions = [];
						for (const transaction of transactions) {
							const signed = await signer.signTransaction(transaction);
							signedTransactions.push(signed);
						}
						return signedTransactions;
					},
				};

				const umiInstance = createUmiInstance(umiSigner);
				setUmi(umiInstance);
				umiInitializedRef.current = currentWalletKey || null;
			} catch (error) {
				console.error("Failed to initialize UMI:", error);
				dispatch({
					type: ACTION_TYPES.SET_ERROR,
					payload: "Failed to initialize wallet connection",
				});
			}
		} else if (!signer || !publicKey) {
			setUmi(null);
			umiInitializedRef.current = null;
		}
	}, [signer, publicKey]);

	// Detect wallet switch and reset all per-wallet data
	useEffect(() => {
		if (!publicKey) return;
		const keyStr = String(publicKey);
		if (prevPublicKeyRef.current !== null && prevPublicKeyRef.current !== keyStr) {
			// New wallet selected — clear stale NFT lists, balance, and tab-load flags
			setLoadedTabs({});
			dispatch({ type: ACTION_TYPES.RESET_STATE });
		}
		prevPublicKeyRef.current = keyStr;
	}, [publicKey]);

	// Helper to extract metadata in a robust way (for wallet display)
	const safelyExtractMetadata = useCallback(
		async (item: any): Promise<any | null> => {
			try {
				if (!item || !item.id || !item.content) return null;
				if (!item.compression || !item.compression.compressed) return null;
				const metadataFromResponse = item.content?.metadata || {};
				const imageUri =
					item.content?.links?.image ||
					item.content?.files?.[0]?.uri ||
					item.content?.files?.[0]?.cdn_uri ||
					"";
				const safeName =
					metadataFromResponse.name || `Compressed NFT #${item.id.slice(0, 8)}`;

				return {
					publicKey: item.id,
					isCompressed: Boolean(item.compression?.compressed),
					metadata_uri: {
						name: safeName,
						image: imageUri,
						publicKey: item.id,
						owner: item.ownership?.owner || publicKey?.toString() || "",
						mint: item.id,
						updateAuthority: item.authorities?.[0]?.address || "",
						attributes: metadataFromResponse.attributes || [],
					},
					mint: { publicKey: item.id },
					metadata: { uri: item.content?.json_uri || "" },
					token: null,
				};
			} catch (error) {
				return null;
			}
		},
		[publicKey]
	);

	// Convert DigitalAsset to NFT format for wallet display
	const convertDigitalAssetToNFT = useCallback((digitalAsset: any): NFT => {
		return {
			publicKey: digitalAsset.publicKey,
			isCompressed: Boolean(digitalAsset.isCompressed),
			metadata_uri: {
				name: digitalAsset.metadata_uri.name,
				image: digitalAsset.metadata_uri.image,
				publicKey: digitalAsset.metadata_uri.publicKey,
				owner: digitalAsset.metadata_uri.owner,
				mint: digitalAsset.metadata_uri.mint,
				updateAuthority: digitalAsset.metadata_uri.updateAuthority,
			},
		};
	}, []);

	// Add unique traits (simplified for wallet - no trait filtering needed)
	const addUniqueTraits = useCallback((traits: any[]) => {
		// Traits tracked for collection metadata
	}, []);

	// Use the optimized CNFT fetch hook
	const {
		fetchCompressedNFTs: optimizedFetchCNFTs,
		progress,
		isLoading: isCNFTOptimizedLoading,
	} = useOptimizedCNFTFetch(safelyExtractMetadata, addUniqueTraits);

	const fetchBalance = useCallback(
		async (publicKeyString: string) => {
			if (!publicKeyString) return;

			try {
				const balanceInLamports = await connection.getBalance(
					new PublicKey(publicKeyString)
				);
				dispatch({
					type: ACTION_TYPES.SET_BALANCE,
					payload: balanceInLamports / LAMPORTS_PER_SOL,
				});
			} catch (err) {
				console.error("Balance fetch error:", err);
				dispatch({
					type: ACTION_TYPES.SET_ERROR,
					payload: "Failed to fetch balance",
				});
			}
		},
		[connection]
	);

	const fetchWalletData = useCallback(
		async (isRefresh = false) => {
			if (!publicKey) return;

			if (isRefresh) {
				dispatch({ type: ACTION_TYPES.SET_REFRESHING, payload: true });
			}

			dispatch({ type: ACTION_TYPES.CLEAR_ERROR });

			try {
				await fetchBalance(publicKey);
			} catch (err) {
				dispatch({
					type: ACTION_TYPES.SET_ERROR,
					payload: "Failed to fetch wallet data",
				});
			} finally {
				if (isRefresh) {
					dispatch({ type: ACTION_TYPES.SET_REFRESHING, payload: false });
				}
			}
		},
		[publicKey, fetchBalance]
	);

	const cancelStandardNftFetch = useCallback(() => {
		if (standardNftFetchAbortRef.current) {
			standardNftFetchAbortRef.current.abort();
			standardNftFetchAbortRef.current = null;
		}
	}, []);

	const fetchStandardNFTsBatched = useCallback(
		async (requestedByUser = false) => {
			if (!publicKey || !umi) return;

			cancelStandardNftFetch();
			const controller = new AbortController();
			standardNftFetchAbortRef.current = controller;
			const requestId = ++standardNftFetchRequestIdRef.current;
			const BATCH_SIZE = 5;

			dispatch({ type: ACTION_TYPES.SET_NFT_LOADING, payload: true });
			dispatch({ type: ACTION_TYPES.CLEAR_ERROR });
			dispatch({ type: ACTION_TYPES.SET_NFTS, payload: [] });
			setNftLoadingProgressText("Loading 0 of 0...");

			try {
				const fetchedStandardNFTs = await fetchAllDigitalAssetWithTokenByOwner(
					umi,
					fromWeb3JsPublicKey(new PublicKey(publicKey))
				);

				if (controller.signal.aborted || requestId !== standardNftFetchRequestIdRef.current)
					return;

				const total = fetchedStandardNFTs.length;
				setNftLoadingProgressText(`Loading 0 of ${total}...`);

				const accumulated: NFT[] = [];
				for (let start = 0; start < total; start += BATCH_SIZE) {
					if (
						controller.signal.aborted ||
						requestId !== standardNftFetchRequestIdRef.current
					) {
						return;
					}

					const chunk = fetchedStandardNFTs.slice(start, start + BATCH_SIZE);
					const chunkResults = await Promise.all(
						chunk.map(async (nft) => {
							try {
								const response = await fetch(nft.metadata.uri, {
									signal: controller.signal,
								});
								if (!response.ok) throw new Error("Failed to fetch metadata");
								const metadata = await response.json();

								return {
									publicKey: nft.publicKey.toString(),
									isCompressed: false,
									metadata_uri: {
										...metadata,
										publicKey: nft.publicKey.toString(),
										owner: publicKey.toString(),
										mint: nft.mint.publicKey.toString(),
										updateAuthority: nft.metadata.updateAuthority.toString(),
									},
								} as NFT;
							} catch (error: any) {
								if (error?.name !== "AbortError") {
									console.error(`Failed to fetch metadata for NFT: ${error}`);
								}
								return null;
							}
						})
					);

					if (
						controller.signal.aborted ||
						requestId !== standardNftFetchRequestIdRef.current
					) {
						return;
					}

					const validChunk = chunkResults.filter((nft): nft is NFT => nft !== null);
					accumulated.push(...validChunk);
					dispatch({ type: ACTION_TYPES.SET_NFTS, payload: [...accumulated] });
					const processed = Math.min(start + chunk.length, total);
					setNftLoadingProgressText(`Loading ${processed} of ${total}...`);
				}
			} catch (err: any) {
				if (err?.name === "AbortError") return;
				console.error("Standard NFT fetch error:", err);
				dispatch({
					type: ACTION_TYPES.SET_ERROR,
					payload: "Failed to fetch standard NFTs",
				});
			} finally {
				if (requestId === standardNftFetchRequestIdRef.current) {
					dispatch({ type: ACTION_TYPES.SET_NFT_LOADING, payload: false });
					setNftLoadingProgressText(null);
					standardNftFetchAbortRef.current = null;
				}
			}

			if (requestedByUser) {
				dispatch({ type: ACTION_TYPES.SET_REFRESHING, payload: false });
			}
		},
		[publicKey, umi, cancelStandardNftFetch]
	);

	// Manual fetch functions that don't cause dependency loops
	const fetchStandardNFTsManual = useCallback(async () => {
		if (!publicKey || !umi) return;
		dispatch({ type: ACTION_TYPES.SET_REFRESHING, payload: true });
		await fetchStandardNFTsBatched(true);
	}, [publicKey, umi, fetchStandardNFTsBatched]);

	const fetchCompressedNFTsManual = useCallback(async () => {
		if (!publicKey || !rpc) return;

		dispatch({ type: ACTION_TYPES.SET_NFT_LOADING, payload: true });
		dispatch({ type: ACTION_TYPES.CLEAR_ERROR });

		try {
			const fetchedNFTs = await optimizedFetchCNFTs();
			const convertedNFTs = fetchedNFTs.map(convertDigitalAssetToNFT);
			dispatch({
				type: ACTION_TYPES.SET_COMPRESSED_NFTS,
				payload: convertedNFTs,
			});
		} catch (error) {
			console.error("Error fetching compressed NFTs:", error);
			dispatch({
				type: ACTION_TYPES.SET_ERROR,
				payload: "Failed to fetch compressed NFTs",
			});
		} finally {
			dispatch({ type: ACTION_TYPES.SET_NFT_LOADING, payload: false });
		}
	}, [publicKey, rpc, optimizedFetchCNFTs, convertDigitalAssetToNFT]);

	// Initial wallet data fetch
	useEffect(() => {
		if (isConnected && publicKey) {
			fetchWalletData();
		} else {
			dispatch({ type: ACTION_TYPES.RESET_STATE });
		}
	}, [isConnected, publicKey, fetchWalletData]);

	// NFT fetching effects - move the logic inside to avoid dependency issues
	useEffect(() => {
		if (
			isConnected &&
			publicKey &&
			activeTab === "nfts" &&
			!loadedTabs["nfts"] &&
			!isNFTLoading &&
			umi
		) {
			setLoadedTabs((prev) => ({ ...prev, nfts: true }));

			// Move the fetching logic directly inside useEffect to avoid dependency loops
			(async () => {
				await fetchStandardNFTsBatched(false);
			})();
		}
	}, [
		isConnected,
		publicKey,
		activeTab,
		loadedTabs,
		isNFTLoading,
		umi,
		fetchStandardNFTsBatched,
	]);

	useEffect(() => {
		return () => {
			cancelStandardNftFetch();
		};
	}, [cancelStandardNftFetch]);

	useEffect(() => {
		if (activeTab !== "nfts") {
			cancelStandardNftFetch();
			setNftLoadingProgressText(null);
		}
	}, [activeTab, cancelStandardNftFetch]);

	useEffect(() => {
		if (
			isConnected &&
			publicKey &&
			activeTab === "cnfts" &&
			!loadedTabs["cnfts"] &&
			!isNFTLoading
		) {
			setLoadedTabs((prev) => ({ ...prev, cnfts: true }));

			// Move the fetching logic directly inside useEffect
			(async () => {
				dispatch({ type: ACTION_TYPES.SET_NFT_LOADING, payload: true });
				dispatch({ type: ACTION_TYPES.CLEAR_ERROR });

				try {
					const fetchedNFTs = await optimizedFetchCNFTs();
					const convertedNFTs = fetchedNFTs.map(convertDigitalAssetToNFT);
					dispatch({
						type: ACTION_TYPES.SET_COMPRESSED_NFTS,
						payload: convertedNFTs,
					});
				} catch (error) {
					console.error("Error fetching compressed NFTs:", error);
					dispatch({
						type: ACTION_TYPES.SET_ERROR,
						payload: "Failed to fetch compressed NFTs",
					});
				} finally {
					dispatch({ type: ACTION_TYPES.SET_NFT_LOADING, payload: false });
				}
			})();
		}
	}, [
		isConnected,
		publicKey,
		activeTab,
		loadedTabs,
		isNFTLoading,
		optimizedFetchCNFTs,
		convertDigitalAssetToNFT,
	]);

	const handleWalletDisconnect = async () => {
		dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
		dispatch({ type: ACTION_TYPES.CLEAR_ERROR });

		try {
			await logout();
			handleToggleDisconnectModal();
		} catch (error) {
			dispatch({
				type: ACTION_TYPES.SET_ERROR,
				payload: "Failed to disconnect wallet",
			});
		} finally {
			dispatch({ type: ACTION_TYPES.SET_LOADING, payload: false });
		}
	};

	const handleToggleDisconnectModal = () => {
		dispatch({ type: ACTION_TYPES.TOGGLE_DISCONNECT_CONFIRM });
	};

	const formatAddress = (address: string): string => {
		if (!address) return "";
		const len = CONFIG.ADDRESS_PREVIEW_LENGTH;
		return `${address.slice(0, len)}...${address.slice(-len)}`;
	};

	const handleCopyAddress = async () => {
		if (publicKey) {
			try {
				await navigator.clipboard.writeText(publicKey.toString());
				dispatch({ type: ACTION_TYPES.SET_COPIED, payload: true });
				setTimeout(() => {
					dispatch({ type: ACTION_TYPES.SET_COPIED, payload: false });
				}, CONFIG.COPY_TIMEOUT);
			} catch (err) {
				console.error("Failed to copy address:", err);
				dispatch({
					type: ACTION_TYPES.SET_ERROR,
					payload: "Failed to copy address to clipboard",
				});
			}
		}
	};

	const handleNFTClick = useCallback(
		(nftPublicKey: string) => {
			router.push(`/wallet/${nftPublicKey}`);
		},
		[router]
	);

	const handleRefresh = useCallback(() => {
		if (!publicKey) return;
		if (activeTab === "nfts") {
			fetchStandardNFTsManual();
		} else if (activeTab === "cnfts") {
			fetchCompressedNFTsManual();
		} else {
			fetchBalance(publicKey);
		}
	}, [
		publicKey,
		activeTab,
		fetchStandardNFTsManual,
		fetchCompressedNFTsManual,
		fetchBalance,
	]);

	const isCardFundingMainnet =
		process.env.NEXT_PUBLIC_ENV === "production";
	const userWalletAddress = publicKey?.toString();
	const handleFundWallet = () => {
		if (!userWalletAddress || !isCardFundingMainnet) return;

		fundWallet({
			address: userWalletAddress,
			options: {
				amount: "0.05",
			},
		});
	};

	const handleTabChange = useCallback((tab: string) => {
		dispatch({ type: ACTION_TYPES.SET_ACTIVE_TAB, payload: tab });
	}, []);

	const handleDirectPhantomConnection = async () => {
		if (!hasPhantomExtension) {
			message.error(
				`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not detected. Please install it first.`
			);
			return;
		}

		setIsConnectingPhantom(true);
		try {
			// Connect directly to Phantom extension
			const phantom = (window as any).solana;
			if (!phantom || !phantom.isPhantom) {
				throw new Error(
					`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not found`
				);
			}

			// Request connection to Phantom
			const response = await phantom.connect();

			if (response.publicKey) {
				// Now use Privy's connect with wallet method to link this to Privy
				await connect("phantom");
			}
		} catch (error: any) {
			console.error(" Direct Phantom connection error:", error);
			message.error(
				error.message ||
					`Failed to connect to ${MYGEOTOKENS_WALLET_DISPLAY_NAME}. Please try again.`
			);
		} finally {
			setIsConnectingPhantom(false);
		}
	};

	const handlePrevPage = () => {
		dispatch({
			type: ACTION_TYPES.SET_CURRENT_PAGE,
			payload: Math.max(1, currentPage - 1),
		});
	};

	const handleNextPage = () => {
		dispatch({
			type: ACTION_TYPES.SET_CURRENT_PAGE,
			payload: Math.min(totalPages, currentPage + 1),
		});
	};

	const handleCompressedPrevPage = () => {
		dispatch({
			type: ACTION_TYPES.SET_COMPRESSED_CURRENT_PAGE,
			payload: Math.max(1, compressedCurrentPage - 1),
		});
	};

	const handleCompressedNextPage = () => {
		dispatch({
			type: ACTION_TYPES.SET_COMPRESSED_CURRENT_PAGE,
			payload: Math.min(totalCompressedPages, compressedCurrentPage + 1),
		});
	};

	const totalPages = Math.ceil(nfts.length / CONFIG.ITEMS_PER_PAGE);
	const paginatedNFTs = useMemo(() => {
		return nfts.slice(
			(currentPage - 1) * CONFIG.ITEMS_PER_PAGE,
			currentPage * CONFIG.ITEMS_PER_PAGE
		);
	}, [nfts, currentPage]);

	const totalCompressedPages = Math.ceil(
		compressedNfts.length / CONFIG.ITEMS_PER_PAGE
	);
	const paginatedCompressedNFTs = useMemo(() => {
		return compressedNfts.slice(
			(compressedCurrentPage - 1) * CONFIG.ITEMS_PER_PAGE,
			compressedCurrentPage * CONFIG.ITEMS_PER_PAGE
		);
	}, [compressedNfts, compressedCurrentPage]);

	const displayError = error;

	return (
		<div className="min-h-screen bg-[#231F20] flex items-center justify-center p-4">
			<div className="w-full max-w-3xl lg:max-w-screen space-y-6">
				<WalletHeader connected={!!isConnected} />

				<div className="bg-[#373132] p-4 md:p-6 rounded-2xl shadow-xl">
					<div className="flex flex-col items-center space-y-4">
						{!isConnected && (
							<DisconnectedWalletPanel
								showPhantomLinker={showPhantomLinker}
								setShowPhantomLinker={setShowPhantomLinker}
								isMobile={isMobile}
								hasPhantomExtension={hasPhantomExtension}
								handleDirectPhantomConnection={handleDirectPhantomConnection}
								isConnectingPhantom={isConnectingPhantom}
							/>
						)}

						{isConnected && publicKey && (
							<div className="w-full">
								{/* Wallet Switcher for desktop users with multiple wallets */}
								{!isMobile && (
									<div className="mb-4">
								<WalletSwitcher
										onWalletChange={() => {
											// Reset handled by the publicKey-change effect above
										}}
									/>
									</div>
								)}

								<WalletBalance
									publicKey={publicKey as any}
									balance={balance}
									copied={copied}
									formatAddress={formatAddress}
									handleCopyAddress={handleCopyAddress}
									handleRefresh={handleRefresh}
									isRefreshing={isRefreshing}
								/>

								<div className="bg-[#231F20] p-3 md:p-4 rounded-lg mb-4">
									<button
										onClick={handleFundWallet}
										disabled={!userWalletAddress || !isCardFundingMainnet}
										className="w-full bg-[#36C47D] hover:bg-[#2FA86B] disabled:bg-[#36C47D]/40 disabled:cursor-not-allowed rounded-xl py-3 px-4 text-[#231F20] font-semibold transition-colors text-sm md:text-base"
									>
										Fund with Apple Pay / Google Pay
									</button>
									{!isCardFundingMainnet && (
										<p className="text-yellow-500 text-sm text-center mt-3">
											Card funding is only available on mainnet.
										</p>
									)}
								</div>

								<WalletTabsPanel
									activeTab={activeTab}
									compressedNfts={compressedNfts}
									handleTabChange={handleTabChange}
									balance={balance}
									isNFTLoading={isNFTLoading}
									nftLoadingProgressText={nftLoadingProgressText}
									paginatedNFTs={paginatedNFTs}
									handleNFTClick={handleNFTClick}
									totalPages={totalPages}
									currentPage={currentPage}
									handlePrevPage={handlePrevPage}
									handleNextPage={handleNextPage}
									handleRefresh={handleRefresh}
									isRefreshing={isRefreshing}
									isCNFTOptimizedLoading={isCNFTOptimizedLoading}
									paginatedCompressedNFTs={paginatedCompressedNFTs}
									totalCompressedPages={totalCompressedPages}
									compressedCurrentPage={compressedCurrentPage}
									handleCompressedPrevPage={handleCompressedPrevPage}
									handleCompressedNextPage={handleCompressedNextPage}
									progress={progress}
								/>
							</div>
						)}

						{displayError && (
							<div
								className="w-full bg-[#E05E5C]/20 border border-[#E05E5C] rounded-lg p-3"
								role="alert"
								aria-live="assertive"
							>
								<p className="text-[#E05E5C] text-xs md:text-sm text-center">
									{displayError}
								</p>
							</div>
						)}

						{isConnected && (
							<button
								onClick={handleToggleDisconnectModal}
								disabled={isLoading}
								className="bg-[#231F20] border border-[#E05E5C] rounded-xl py-3 px-4 md:px-5 text-[#E05E5C] hover:bg-[#E05E5C]/10
                       transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                       w-full flex items-center justify-center space-x-2 text-sm md:text-base"
								aria-label="Disconnect wallet"
							>
								<LogOut size={18} className="mr-2" />
								<span>
									{isLoading ? "Disconnecting..." : "Disconnect Wallet"}
								</span>
							</button>
						)}
					</div>
				</div>
			</div>

			{showDisconnectConfirm && (
				<DisconnectConfirmDialog
					onConfirm={handleWalletDisconnect}
					onCancel={handleToggleDisconnectModal}
					isLoading={isLoading}
				/>
			)}
		</div>
	);
};

export default WalletUI;
